import { useExerciseAttempts } from './useExerciseAttempts';
import type { SubmissionData } from './exerciseTypes';
import type { OutcomeKind } from '../../components/ExerciseOutcomeActions';

function isGrading(submission: SubmissionData | null): boolean {
  return Boolean(submission?.status && submission.status !== 'DONE');
}

function outcomeKind(verdict: SubmissionData | null, attemptsExhausted: boolean): OutcomeKind | null {
  if (verdict?.passed === true) {
    return 'passed';
  }
  if (verdict?.passed === false) {
    return attemptsExhausted ? 'exhausted' : 'retryable';
  }
  return null;
}

/**
 * Derives what the exercise page should show and allow from the latest submission: the verdict
 * (only once graded), whether the exercise is closed (solved or out of attempts), whether the
 * inputs must be disabled (grading in progress, pause after a wrong answer, or closed), and which
 * follow-up actions apply.
 */
export function useExerciseOutcome(submission: SubmissionData | null, isSubmitting: boolean) {
  const { cooldownSeconds, attemptsExhausted } = useExerciseAttempts(submission);

  const isPending = isGrading(submission);
  const verdict = isPending ? null : submission;
  const kind = outcomeKind(verdict, attemptsExhausted);

  const locked = kind === 'passed' || attemptsExhausted;
  const inputsDisabled = isSubmitting || isPending || locked || cooldownSeconds > 0;

  return { verdict, kind, locked, inputsDisabled, isPending, cooldownSeconds, attemptsExhausted };
}
